import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutInfo } from '@/mocks/data';

export default function About() {
  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': '豆花视频',
    'url': 'https://www.douhuavideo.com',
    'description': '豆花视频为你聚合香蕉视频、红桃视频、蜜桃视频等美食命名视频平台，免费高清在线观看电视剧全集和最新电影。',
    'foundingDate': '2023',
    'contactPoint': {
      '@type': 'ContactPoint',
      'telephone': aboutInfo.contact.phone,
      'contactType': 'customer service',
      'availableLanguage': 'Chinese',
      'email': aboutInfo.contact.email,
    },
    'address': {
      '@type': 'PostalAddress',
      'streetAddress': '豆花路88号豆花大厦',
      'addressLocality': '北京',
      'addressRegion': '朝阳区',
      'addressCountry': 'CN',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': [
      {
        '@type': 'Question',
        'name': '豆花视频是免费的吗？',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': '是的，豆花视频完全免费。我们聚合了香蕉视频、红桃视频、蜜桃视频等多个平台的内容，所有视频均免费在线观看，无需付费。',
        },
      },
      {
        '@type': 'Question',
        'name': '豆花视频支持哪些设备观看？',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': '豆花视频支持手机、平板、电脑等多种设备观看。只需打开浏览器访问即可，无需下载任何APP。',
        },
      },
      {
        '@type': 'Question',
        'name': '视频播放卡顿怎么办？',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': '豆花视频采用先进的CDN加速技术，确保流畅播放。如遇卡顿，建议检查网络连接或切换清晰度。一般情况下不会出现缓冲问题。',
        },
      },
      {
        '@type': 'Question',
        'name': '豆花视频的内容更新频率如何？',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': '豆花视频每日更新最新剧集和电影，确保用户能够第一时间观看到最新的内容。热门剧集通常在播出后24小时内更新。',
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb
              items={[{ label: '关于我们' }]}
            />
          </div>

          {/* Hero */}
          <section className="relative px-4 md:px-6 py-16 md:py-20 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20cozy%20team%20workspace%20with%20people%20collaborating%2C%20modern%20office%20with%20plants%20and%20natural%20light%2C%20creative%20atmosphere%2C%20golden%20hour%20lighting%2C%20friendly%20collaborative%20environment&width=1600&height=500&seq=douhua-about-hero&orientation=landscape"
              alt="关于豆花视频"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
            <div className="relative z-10 max-w-2xl mx-auto text-center">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 font-heading">
                关于豆花视频
              </h1>
              <p className="text-sm md:text-base text-white/80 leading-relaxed">
                以美食之名，聚精彩视频
              </p>
            </div>
          </section>

          {/* Brand Story */}
          <section className="px-4 md:px-6 py-12">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-foreground-900 mb-6 font-heading text-center">
                品牌故事
              </h2>
              <p className="text-base text-foreground-700 leading-relaxed text-center max-w-3xl mx-auto">
                {aboutInfo.brandStory}
              </p>
            </div>
          </section>

          {/* Stats */}
          <section className="px-4 md:px-6 py-12 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                {aboutInfo.stats.map((stat) => (
                  <div key={stat.label} className="text-center p-5 rounded-lg bg-background-50 border border-background-200/70">
                    <p className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">
                      {stat.number}
                    </p>
                    <p className="text-sm text-foreground-500 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Mission & Values */}
          <section className="px-4 md:px-6 py-12">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl font-bold text-foreground-900 mb-3 font-heading">
                  我们的使命
                </h2>
                <p className="text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto">
                  {aboutInfo.mission}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {aboutInfo.values.map((v) => (
                  <div key={v.title} className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                    <h4 className="text-base font-bold text-foreground-900 mb-2 font-heading">{v.title}</h4>
                    <p className="text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="px-4 md:px-6 py-12 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-foreground-900 mb-8 font-heading text-center">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {aboutInfo.reputation.map((r) => (
                  <div key={r.user} className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                    <div className="flex items-center gap-1 mb-2">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="w-4 h-4 flex items-center justify-center">
                          <i className={`text-sm ${i < r.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-foreground-300'}`}></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                      &ldquo;{r.comment}&rdquo;
                    </p>
                    <p className="text-xs font-medium text-foreground-500">—— {r.user}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="px-4 md:px-6 py-12">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-foreground-900 mb-8 font-heading text-center">
                联系我们
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-foreground-900 mb-1 font-heading">电子邮箱</h4>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-500">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-foreground-900 mb-1 font-heading">客服电话</h4>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-500">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-foreground-900 mb-1 font-heading">公司地址</h4>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-500">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-foreground-900 mb-1 font-heading">工作时间</h4>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.workHours}</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-lg bg-background-100 border border-background-200/70">
                  <h4 className="text-base font-bold text-foreground-900 mb-4 font-heading">
                    发送消息
                  </h4>
                  <form
                    className="space-y-4"
                    data-readdy-form="contact"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const form = e.currentTarget;
                      const formData = new FormData(form);
                      const honeypot = formData.get('website_alt') as string;
                      const msgEl = document.getElementById('about-contact-msg');
                      if (honeypot && honeypot.trim() !== '') {
                        if (msgEl) { msgEl.textContent = '消息已发送，我们会尽快回复您！'; msgEl.className = 'text-sm text-secondary-500 mt-2'; }
                        form.reset();
                        return;
                      }
                      try {
                        const url = 'https://form.readdy.ai/submit';
                        const res = await fetch(url, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                          body: new URLSearchParams(
                            Array.from(formData.entries())
                              .filter(([k]) => k !== 'website_alt')
                              .map(([k, v]) => [k, typeof v === 'string' ? v : ''])
                          ).toString(),
                        });
                        if (res.ok) {
                          const txt = await res.text();
                          let parsed;
                          try { parsed = JSON.parse(txt); } catch { parsed = null; }
                          if (parsed && parsed.code === 'OK') {
                            if (msgEl) { msgEl.textContent = '消息已发送，我们会尽快回复您！'; msgEl.className = 'text-sm text-secondary-500 mt-2'; }
                            form.reset();
                          } else {
                            const errMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试。';
                            if (msgEl) { msgEl.textContent = errMsg; msgEl.className = 'text-sm text-red-500 mt-2'; }
                          }
                        } else {
                          const txt = await res.text();
                          let msg = '提交失败，请稍后再试。';
                          if (txt.includes('spam')) msg = '提交失败，请稍后重试。';
                          if (msgEl) { msgEl.textContent = msg; msgEl.className = 'text-sm text-red-500 mt-2'; }
                        }
                      } catch {
                        if (msgEl) { msgEl.textContent = '网络错误，请稍后再试。'; msgEl.className = 'text-sm text-red-500 mt-2'; }
                      }
                    }}
                  >
                    <div>
                      <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        您的姓名
                      </label>
                      <input
                        type="text"
                        id="about-name"
                        name="name"
                        required
                        className="w-full px-3 py-2 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        电子邮箱
                      </label>
                      <input
                        type="email"
                        id="about-email"
                        name="email"
                        required
                        className="w-full px-3 py-2 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="about-message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-3 py-2 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="website_alt"
                      className="spam-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <button
                      type="submit"
                      className="w-full py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      发送消息
                    </button>
                    <p id="about-contact-msg" className="text-sm text-secondary-500 mt-2"></p>
                  </form>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="px-4 md:px-6 py-12 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold text-foreground-900 mb-8 font-heading text-center">
                常见问题
              </h2>
              <div className="space-y-3">
                {faqSchema.mainEntity.map((faq, idx) => (
                  <details key={idx} className="group rounded-lg bg-background-50 border border-background-200/70 overflow-hidden">
                    <summary className="flex items-center justify-between p-5 cursor-pointer text-sm font-medium text-foreground-900 hover:text-primary-500 transition-colors">
                      {faq.name}
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className="ri-arrow-down-s-line group-open:hidden"></i>
                        <i className="ri-arrow-up-s-line hidden group-open:block"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.acceptedAnswer.text}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}